import { useCallback, useEffect } from "react";
import create, { UseStore } from "zustand";
import {
	MediaQueryKind,
	useIsTouchDevice,
	useLocalStorageState,
	useUserScopedKey,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { shallowComparison } from "client/Misc/compareUtils";
import { MenuItemKind } from "./GlobalNavigationSidebar";

const globalNavigationPinnedDesktopKey = "global-navigation:desktop-pinned-state";

export type IsPinnedState = {
	isPinned: boolean;
	setIsPinned: (isPinned: boolean) => void;
};

export type HoveredItemState = {
	hoveredItem: MenuItemKind | undefined;
	setHoveredItem: (hoveredItem: MenuItemKind | undefined) => void;
};

export type GlobalNavigationVisibilityStoreData = IsPinnedState & HoveredItemState;

const useGlobalNavigationVisibilityStore: UseStore<GlobalNavigationVisibilityStoreData> = create(
	(set) => ({
		isPinned: false,
		setIsPinned: (isPinned: boolean) => set({ isPinned }),
		hoveredItem: undefined,
		setHoveredItem: (hoveredItem: MenuItemKind | undefined) => set({ hoveredItem })
	})
);

export function useGlobalNavigationSidebarPinnedState(): IsPinnedState {
	const globalNavigationPinnedDesktopPersistedKey: string =
		useUserScopedKey(globalNavigationPinnedDesktopKey);

	const [
		isExpandedOnDesktop,
		setIsExpandedOnDesktop
	] = useLocalStorageState<boolean>(globalNavigationPinnedDesktopPersistedKey, true);

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	const { isPinned, setIsPinned } = useGlobalNavigationVisibilityStore(
		x => ({
			isPinned: x.isPinned,
			setIsPinned: x.setIsPinned
		}),
		shallowComparison
	);

	useEffect(
		() => {
			setIsPinned(isResponsive ? false : isExpandedOnDesktop);
		},
		// setting initial value of isPinned
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const setIsPinnedHandler = useCallback(
		(isPinned: boolean) => {
			setIsPinned(isPinned);

			if (!isResponsive) {
				setIsExpandedOnDesktop(isPinned);
			}
		},
		[
			isResponsive,
			setIsExpandedOnDesktop,
			setIsPinned
		]
	);

	return {
		isPinned,
		setIsPinned: setIsPinnedHandler
	};
}

export function useHoveredItemState(): HoveredItemState {
	const { hoveredItem, setHoveredItem } = useGlobalNavigationVisibilityStore(
		x => ({
			hoveredItem: x.hoveredItem,
			setHoveredItem: x.setHoveredItem
		}),
		shallowComparison
	);

	return {
		hoveredItem,
		setHoveredItem
	};
}

export function useHandleNavigationInGlobalSubNavigation(): () => void {
	const {
		setHoveredItem,
		setIsPinned
	} = useGlobalNavigationVisibilityStore(
		x => ({
			setHoveredItem: x.setHoveredItem,
			setIsPinned: x.setIsPinned
		}),
		shallowComparison
	);

	const isTouch: boolean = useIsTouchDevice();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	const handleNavigationInGlobalSubNavigation = useCallback(
		(): void => {
			if (isResponsive) {
				setIsPinned(false);
			}

			if (isTouch) {
				setHoveredItem(undefined);
			}
		},
		[
			isResponsive,
			isTouch,
			setHoveredItem,
			setIsPinned
		]
	);

	return handleNavigationInGlobalSubNavigation;
}